import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sunrise, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password strength checks
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    match: password.length > 0 && password === confirmPassword,
  };
  const allValid = Object.values(checks).every(Boolean);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/validate-invite?token=${token}`)
      .then(res => {
        setInviteInfo(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-invite`, { token, password });
      setSuccess(true);
      toast.success('Account activated! Redirecting to login...');
      const clientIdParam = inviteInfo?.client_id ? `?client_id=${encodeURIComponent(inviteInfo.client_id)}&email=${encodeURIComponent(inviteInfo.email || '')}` : '';
      setTimeout(() => navigate(`/login${clientIdParam}`), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate account');
    } finally {
      setSubmitting(false);
    }
  };

  const PasswordCheck = ({ valid, label }) => (
    <div className="flex items-center gap-2 text-sm" data-testid={`check-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {valid ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      ) : (
        <XCircle className="h-4 w-4 text-slate-300" />
      )}
      <span className={valid ? 'text-emerald-600' : 'text-slate-400'}>{label}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-500">Validating invitation...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="w-full max-w-md" data-testid="invite-error">
          <CardHeader className="text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <CardTitle className="text-xl text-red-600">Invalid Invitation</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-600 mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate('/login')}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="w-full max-w-md" data-testid="invite-success">
          <CardHeader className="text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <CardTitle className="text-xl text-emerald-700">Account Activated!</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-slate-600 mb-2">Your account has been set up. Redirecting to login...</p>
            {inviteInfo?.client_id && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-left text-xs text-slate-700">
                <p className="font-semibold mb-1">When you sign in, use:</p>
                <p>Client ID: <span className="font-mono font-semibold">{inviteInfo.client_id}</span></p>
                <p>Email: <span className="font-semibold">{inviteInfo.email}</span></p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="invite-accept-card">
        <CardHeader className="space-y-3">
          <div className="flex flex-col items-center justify-center mb-2">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 rounded-lg mb-3 shadow-lg">
              <Sunrise className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">EQUIPR Horizon</h1>
          </div>
          <CardTitle className="text-xl font-semibold text-center text-slate-700">
            Set Up Your Account
          </CardTitle>
          <div className="text-center space-y-1">
            <p className="text-sm text-slate-500">
              Welcome to <strong className="text-slate-700">{inviteInfo?.tenant_name}</strong>
            </p>
            {inviteInfo?.client_id && (
              <p className="text-sm text-slate-500">
                Client ID: <strong className="text-slate-700 font-mono">{inviteInfo.client_id}</strong>
              </p>
            )}
            <p className="text-sm text-slate-500">
              Email: <strong className="text-slate-700">{inviteInfo?.email}</strong>
            </p>
            <p className="text-sm text-slate-500">
              Role: <strong className="text-slate-700">{inviteInfo?.role}</strong>
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="invite-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-10 border-slate-300 pr-10"
                  placeholder="Create a secure password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-semibold text-slate-700">Confirm Password</Label>
              <Input
                id="confirm-password"
                type={showPassword ? 'text' : 'password'}
                data-testid="invite-confirm-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-10 border-slate-300"
                placeholder="Confirm your password"
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5" data-testid="password-requirements">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Password Requirements</p>
              <PasswordCheck valid={checks.length} label="At least 8 characters" />
              <PasswordCheck valid={checks.uppercase} label="One uppercase letter" />
              <PasswordCheck valid={checks.lowercase} label="One lowercase letter" />
              <PasswordCheck valid={checks.number} label="One number" />
              <PasswordCheck valid={checks.match} label="Passwords match" />
            </div>

            <Button
              type="submit"
              data-testid="activate-account-button"
              disabled={!allValid || submitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-md h-10 font-medium transition-colors disabled:opacity-50"
            >
              {submitting ? 'Activating...' : 'Activate Account'}
            </Button>
          </form>
          <p className="mt-4 text-xs text-center text-slate-400">
            Already activated? <a href="/login" className="text-amber-600 hover:underline">Sign in</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
